import { useEffect, useState } from "react";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right.mjs";
import Bell from "lucide-react/dist/esm/icons/bell.mjs";
import CalendarDays from "lucide-react/dist/esm/icons/calendar-days.mjs";
import Check from "lucide-react/dist/esm/icons/check.mjs";
import Circle from "lucide-react/dist/esm/icons/circle.mjs";
import Clock3 from "lucide-react/dist/esm/icons/clock-3.mjs";
import LogOut from "lucide-react/dist/esm/icons/log-out.mjs";
import MessageSquareText from "lucide-react/dist/esm/icons/message-square-text.mjs";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check.mjs";
import { devLogin, getMe, logout } from "./lib/api";
import { stagesByTier, tierLabels } from "./lib/stages";
import type { PortalClient, Tier } from "./lib/types";

const previewClient: PortalClient = {
  id: "preview",
  firstName: "Jordan",
  businessName: "Northstar Mechanical",
  tier: "tier_1",
  currentStage: "production",
  nextUpdate: "Tomorrow by 4:00 PM",
};

function BrandMark() {
  return <div className="brand-mark"><img src="/acquisition-labs-logo.webp" alt="Acquisition Labs" /></div>;
}

function Login({ onLogin, onPreview }: { onLogin: (email: string, code: string) => Promise<void>; onPreview: () => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return <main className="login-page">
    <section className="login-story">
      <BrandMark />
      <div className="story-copy">
        <p className="eyebrow">Client operations portal</p>
        <h1>Know what’s moving.<br /><em>Know what’s next.</em></h1>
        <p>Your campaign, onboarding milestones, and next actions—clear, current, and in one place.</p>
      </div>
      <div className="story-signal"><span>01</span><span>Connect</span><span>Build</span><span>Launch</span></div>
    </section>
    <section className="login-panel">
      <form onSubmit={async (event) => {
        event.preventDefault(); setBusy(true); setError("");
        try { await onLogin(email, code); } catch (reason) { setError(reason instanceof Error ? reason.message : "Login failed."); } finally { setBusy(false); }
      }}>
        <div className="secure-label"><ShieldCheck size={17} /> Secure client access</div>
        <h2>Welcome back</h2>
        <p>Enter the email on your Acquisition Labs account and your access code.</p>
        <label>Email address<input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" /></label>
        <label>Access code<input type="password" required autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="••••••••" /></label>
        {error && <div className="form-error" role="alert">{error}</div>}
        <button className="primary-button" disabled={busy}>{busy ? "Checking access…" : "Open my portal"}<ArrowRight size={18} /></button>
        <button className="preview-button" type="button" onClick={onPreview}>Preview dashboard</button>
        <small>Phase 1 uses a development access code. Production email delivery is intentionally left for the next phase.</small>
      </form>
    </section>
  </main>;
}

function Dashboard({ client, onLogout, onTierChange }: { client: PortalClient; onLogout: () => void; onTierChange?: (tier: Tier) => void }) {
  const stages = stagesByTier[client.tier];
  const currentIndex = Math.max(0, stages.findIndex((stage) => stage.key === client.currentStage));
  const progress = Math.round(((currentIndex + 1) / stages.length) * 100);
  const current = stages[currentIndex];
  return <div className="app-shell">
    <aside>
      <BrandMark />
      <nav aria-label="Primary navigation">
        <a className="active" href="#overview"><Circle size={16} fill="currentColor" /> Overview</a>
        <a href="#onboarding"><Check size={17} /> Onboarding</a>
        <a href="#appointments"><CalendarDays size={17} /> Appointments</a>
        <a href="#updates"><MessageSquareText size={17} /> Updates</a>
      </nav>
      <div className="account-card"><span>{client.firstName[0]}{client.businessName[0]}</span><div><strong>{client.businessName}</strong><small>{tierLabels[client.tier]}</small></div></div>
      <button className="logout" onClick={onLogout}><LogOut size={16} /> Sign out</button>
    </aside>
    <main className="dashboard" id="overview">
      <header><div><p className="eyebrow">Monday, August 17</p><h1>Good morning, {client.firstName}.</h1><p>Here’s where your system stands today.</p></div><button className="icon-button" aria-label="Notifications"><Bell size={19} /><span /></button></header>
      {onTierChange && <div className="preview-switch" aria-label="Preview tier"><span>Preview track</span><button className={client.tier === "tier_1" ? "selected" : ""} onClick={() => onTierChange("tier_1")}>Tier 1</button><button className={client.tier === "tier_2" ? "selected" : ""} onClick={() => onTierChange("tier_2")}>Tier 2</button></div>}
      <section className="hero-card">
        <div className="hero-copy"><span className="status-chip">In motion</span><p>Your current stage</p><h2>{current.label}</h2><p>{current.summary}. We’ll keep this page current as the team moves your work forward.</p><div className="next-update"><Clock3 size={18} /><span><small>Next update</small><strong>{client.nextUpdate}</strong></span></div></div>
        <div className="progress-orbit" style={{ "--progress": `${progress * 3.6}deg` } as React.CSSProperties}><div><strong>{progress}%</strong><span>complete</span></div></div>
      </section>
      <div className="dashboard-grid">
        <section className="stage-card" id="onboarding"><div className="section-heading"><div><p className="eyebrow">Your path</p><h2>{tierLabels[client.tier]}</h2></div><span>{currentIndex + 1} of {stages.length}</span></div><div className="stage-list">{stages.map((stage, index) => {
          const state = index < currentIndex ? "complete" : index === currentIndex ? "current" : "upcoming";
          return <div className={`stage-row ${state}`} key={stage.key}><div className="stage-node">{state === "complete" ? <Check size={14} /> : <span />}</div><div><strong>{stage.label}</strong><p>{stage.summary}</p></div>{state === "current" && <em>Now</em>}</div>;
        })}</div></section>
        <div className="side-stack">
          <section className="action-card"><p className="eyebrow">What we need from you</p><h2>{client.tier === "tier_1" ? "Final creative feedback" : "Brand direction approval"}</h2><p>Review the latest direction so production can keep moving without delay.</p><button>Review next step <ArrowRight size={17} /></button></section>
          <section className="appointment-card" id="appointments"><CalendarDays size={20} /><div><p className="eyebrow">Next appointment</p><h3>{client.tier === "tier_1" ? "Campaign readiness call" : "Creative direction review"}</h3><p>Thursday · 2:30 PM ET</p></div></section>
          <section className="system-card"><div className="section-heading"><div><p className="eyebrow">System check</p><h2>Launch readiness</h2></div></div>{[
            ["Business intake", "Ready"], ["Account access", "Connected"], [client.tier === "tier_1" ? "A2P registration" : "Creative direction", "In review"], ["Campaign", "Not live"],
          ].map(([label, status]) => <div className="system-row" key={label}><span>{label}</span><strong className={status.toLowerCase().replace(" ", "-")}>{status}</strong></div>)}</section>
        </div>
      </div>
    </main>
  </div>;
}

export function App() {
  const [client, setClient] = useState<PortalClient | null | undefined>(undefined);
  const [preview, setPreview] = useState(false);
  useEffect(() => { void getMe().then(setClient).catch(() => setClient(null)); }, []);
  if (client === undefined) return <div className="loading"><BrandMark /><span>Loading your workspace…</span></div>;
  if (!client) return <Login onLogin={async (email, code) => { await devLogin(email, code); setClient(await getMe()); }} onPreview={() => { setPreview(true); setClient(previewClient); }} />;
  return <Dashboard client={client} onLogout={async () => { if (!preview) await logout(); setPreview(false); setClient(null); }} onTierChange={preview ? (tier) => setClient({ ...client, tier, currentStage: tier === "tier_1" ? "production" : "internal-review" }) : undefined} />;
}
